// import 'ag-grid-enterprise/styles/ag-grid.css';
// import 'ag-grid-enterprise/styles/ag-theme-alpine.css';
import { ColDef, GridApi } from 'ag-grid-enterprise';
import { objectFromLogData } from 'dto/logModal/logToTable';
import { ILogFromServer, ILogRow } from 'interfaces/logRow.interface';
import { MutableRefObject, useEffect, useMemo, useRef, useState } from 'react';
import style from './LogModal.module.scss';
import CsbErrorBoundary from 'components/CsbErrorBoudary/CsbErrorBoundary';
import { consoleErrorMessage } from 'utils/commonFunctions/CommonFunctions';
import { TABLE_FIELDS } from 'utils/common-constants';
import { PAGE_OPTIONS } from 'interfaces/table.interface';
import { defaultFilterParams } from 'components/TableContainer/utils/filterUtils';
import { LogTable } from 'components/TableContainer/LogTable/LogTable';
import { IOrchestrationRow } from 'interfaces/dashboard/orchestrationRow.interface';
import { postViewLog } from 'api/orchestrations/orchestrationsThunk';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import { selectPropertiesPanel } from 'redux/propertiesPanel/PropertiesPanelSlice';
import { DateUtils } from 'utils/dateUtils/DateUtils';

interface ILogModal {
  data?: IOrchestrationRow;
}

export const dateFilterPredicate = (
  filterValue: Date | Date[] | string | string[],
  cellValue: string,
  filterType: 'greaterThan' | 'lessThan' | 'inRange'
) => {
  if (cellValue == null) {
    return false;
  }

  // Parse the full date-time string (format: "m/d/yyyy, h:mm:ss") - same as comparator
  const cellDate = new Date(cellValue);
  if (isNaN(cellDate.getTime())) {
    return false;
  }

  switch (filterType) {
    case 'greaterThan':
      const greaterThanDate =
        filterValue instanceof Date
          ? filterValue
          : new Date(filterValue as string);
      if (isNaN(greaterThanDate.getTime())) {
        return false;
      }
      return cellDate > greaterThanDate;

    case 'lessThan':
      const lessThanDate =
        filterValue instanceof Date
          ? filterValue
          : new Date(filterValue as string);
      if (isNaN(lessThanDate.getTime())) {
        return false;
      }
      return cellDate < lessThanDate;

    case 'inRange':
      if (!Array.isArray(filterValue) || filterValue.length !== 2) {
        return false;
      }

      const startDate =
        filterValue[0] instanceof Date
          ? filterValue[0]
          : new Date(filterValue[0] as string);
      const endDate =
        filterValue[1] instanceof Date
          ? filterValue[1]
          : new Date(filterValue[1] as string);

      if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
        return false;
      }

      return cellDate >= startDate && cellDate <= endDate;

    default:
      return false;
  }
};

export default function LogModal({ data }: ILogModal) {
  const [rowData, setRowData] = useState<ILogRow[]>([]);
  const orchestrationStepsInfo = useAppSelector(selectPropertiesPanel);
  let sortingDirection: any = null;
  const CREATED_TIME = 'Created time';
  const logRef = useRef<any>();
  const dispatch = useAppDispatch();
  const [columnDefs] = useState<ColDef[]>([
    {
      field: 'Step name',
      rowGroup: true,
      hide: true,
    },
    {
      field: TABLE_FIELDS.createdTime,
      minWidth: 200,
      suppressSizeToFit: true,
      filter: 'agDateColumnFilter',
      menuTabs: ['filterMenuTab'],
      valueFormatter: ({ value }) => DateUtils.formatUtcToLocalTimezone(value),
      filterParams: {
        ...defaultFilterParams,
        filterOptions: [
          {
            displayKey: 'greaterThan',
            displayName: 'After',
            predicate: (filterValue: Date | string, cellValue: string) =>
              dateFilterPredicate(filterValue, cellValue, 'greaterThan'),
          },
          {
            displayKey: 'lessThan',
            displayName: 'Before',
            predicate: (filterValue: Date | string, cellValue: string) =>
              dateFilterPredicate(filterValue, cellValue, 'lessThan'),
          },
          {
            displayKey: 'inRange',
            displayName: 'Between',
            predicate: (filterValue: Date[] | string[], cellValue: string) =>
              dateFilterPredicate(filterValue, cellValue, 'inRange'),
            numberOfInputs: 2,
          },
        ],
      },
    },
    {
      field: TABLE_FIELDS.message,
      minWidth: 160,
      suppressSizeToFit: false,
      wrapText: true,
      autoHeight: true,
      filter: false,
      menuTabs: [],
      cellRenderer: 'cellLogMessage',
    },
    {
      field: TABLE_FIELDS.severity,
      minWidth: 160,
      cellRenderer: 'errorLogCellStatus',
      cellClass: 'csb-centered-cell csb-visible-cell',
      suppressSizeToFit: true,
      filter: true,
      menuTabs: ['filterMenuTab'],
      filterParams: {
        buttons: ['reset'],
      },
    },
  ]);

  const autoGroupColumnDef = useMemo(() => {
    return {
      headerName: 'Step name',
      minWidth: 180,
      sortable: true,
      suppressSizeToFit: true,
      filter: true,
      menuTabs: ['filterMenuTab'],
      filterParams: {
        ...defaultFilterParams,
      },
      filterValueGetter: (params: any) => params.data['Step name'],
      cellRendererParams: {
        suppressCount: true,
      },
    };
  }, []);

  useEffect(() => {
    fetchLogInfo();
    changeParentOverflowStyle();
  }, []);

  const changeParentOverflowStyle = () => {
    if (logRef?.current?.parentElement) {
      logRef.current.parentElement.style.overflowY = 'hidden';
    }
  };

  const fetchLogInfo = async () => {
    try {
      const logInfo: ILogFromServer[] = await dispatch(
        postViewLog({
          CSBJobId: data?.id,
          fromTimeCSB: data?.originalStartTime,
        })
      )?.unwrap();
      const logRowObject = objectFromLogData(
        logInfo,
        orchestrationStepsInfo,
        data?.originalStartTime
      );
      setRowData(logRowObject);
      document.dispatchEvent(
        new CustomEvent('logInfoFetched', { detail: { id: data?.id } })
      );
    } catch (error) {
      consoleErrorMessage(error);
      document.dispatchEvent(
        new CustomEvent('logInfoFetched', { detail: { id: data?.id } })
      );
    }
  };

  const openSeverityErrorRows = (
    gridApiRef: MutableRefObject<GridApi | null>
  ) => {
    const errorRowsIndex: number[] = [];
    gridApiRef.current?.forEachLeafNode((row: any) => {
      const parentIndex = row.parent?.rowIndex;
      if (
        row.data['severity'] === 'ERROR' &&
        parentIndex != null &&
        !errorRowsIndex.includes(parentIndex)
      ) {
        errorRowsIndex.push(parentIndex);
      }
    });
    setTimeout(
      () =>
        errorRowsIndex.map((index: number) => {
          gridApiRef.current?.getDisplayedRowAtIndex(index)?.setExpanded(true);
        }),
      100
    );
  };

  const sortAccordionRows = (rowNodes: any[]) => {
    if (
      rowNodes?.[0]?.key &&
      rowNodes?.[0]?.['allLeafChildren']?.[0]?.['data']?.[CREATED_TIME]
    ) {
      if (sortingDirection != null) {
        if (sortingDirection === 'asc') {
          rowNodes.sort(
            (a, b) =>
              getMaxMinValue(a?.['allLeafChildren'], 'min') -
              getMaxMinValue(b?.['allLeafChildren'], 'min')
          );
          sortingDirection = 'desc';
        } else {
          rowNodes.sort(
            (a, b) =>
              getMaxMinValue(b?.['allLeafChildren'], 'max') -
              getMaxMinValue(a?.['allLeafChildren'], 'max')
          );
          sortingDirection = null;
        }
      } else {
        sortingDirection = 'asc';
      }
    }
  };

  const getMaxMinValue = (childrens: any[], value: 'max' | 'min') => {
    if (value === 'max') {
      return Math.max.apply(
        Math,
        childrens.map((e: any) => {
          return Date.parse(e.data[CREATED_TIME]);
        })
      );
    } else {
      return Math.min.apply(
        Math,
        childrens.map((e: any) => {
          return Date.parse(e.data[CREATED_TIME]);
        })
      );
    }
  };

  return (
    <CsbErrorBoundary>
      <div
        data-testid="LogModal"
        className={style['error-log-modal']}
        ref={logRef}
      >
        <LogTable
          items={rowData}
          columns={columnDefs}
          additionalData={{
            autoGroupColumnDef,
            postSort: sortAccordionRows,
          }}
          additionalStyles={'wk-striped-grey'}
          onGridReadyAdditionalMethods={[
            { id: 'openSeverityRows', method: openSeverityErrorRows },
          ]}
          page={PAGE_OPTIONS.logs}
        />
      </div>
    </CsbErrorBoundary>
  );
}
